import { useCallback } from 'react';
import { TableV3 } from 'common/table';
import {
  useSourcingInternalSelectedCandidateProfileTableColumns,
  useSourcingInternalSelectedCandidatesForTable
} from 'modules/sourcing-internal/tables/hooks';
import { Header } from 'common/ui/Header';
import { AlertCard } from 'common/ui/AlertCard';
import { Button } from 'system/atoms';
import { useSourcingInternalStore } from 'modules/sourcing-internal/stores/hooks/useSourcingInternalStore';
import { useShallow } from 'zustand/react/shallow';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useNavigate } from 'react-router-dom';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

export const SourcingInternalSelectedCandidateProfilesTable: React.FC = () => {
  const navigate = useNavigate();
  const {
    sessionId,
    candidateBatch,
    onSelectedCandidatesNext,
    onRemoveSelectedCandidate
  } = useSourcingInternalStore(
    useShallow((state) => ({
      sessionId: state.sessionId,
      candidateBatch: state.candidateBatch,
      onSelectedCandidatesNext: state.onSelectedCandidatesNext,
      onRemoveSelectedCandidate: state.onRemoveSelectedCandidate
    }))
  );

  const sourcingChannelSelectionEnabled = useFlag(
    Flags.SOURCING_CHANNEL_SELECTION
  );
  const organization = useOrganization();

  const {
    selectedCandidatesColumnData,
    selectedCandidatesFullData,
    isSelectedCandidatesLoading,
    duplicateCandidateProfileIds,
    refetchSelectedCandidateProfiles
  } = useSourcingInternalSelectedCandidatesForTable({
    sessionId,
    candidateBatchId: candidateBatch?.id ?? null
  });

  const columns = useSourcingInternalSelectedCandidateProfileTableColumns({
    onRemoveSelectedCandidate,
    refetch: refetchSelectedCandidateProfiles
  });

  const handleConfirmAndContinue = useCallback(async () => {
    // Deselect all already invited/applied candidates
    const duplicateSelectionIds = selectedCandidatesFullData
      .filter((profile) =>
        duplicateCandidateProfileIds.includes(
          profile.candidateProfile?.id ?? ''
        )
      )
      .map((profile) => profile.id);

    if (duplicateSelectionIds.length > 0) {
      await Promise.all(
        duplicateSelectionIds.map((selectionId) =>
          onRemoveSelectedCandidate(selectionId)
        )
      );
    }

    const eligibleCandidates = selectedCandidatesFullData.filter(
      (selectedCandidateProfile) =>
        !duplicateCandidateProfileIds.includes(
          selectedCandidateProfile.candidateProfile?.id ?? ''
        )
    );

    // Proceed with only eligible candidates
    onSelectedCandidatesNext({
      isChannelSelectionFeatureFlagEnabled:
        sourcingChannelSelectionEnabled ?? false,
      organizationChannelsEnabled: organization?.channelsEnabled ?? [],
      selectedCandidateProfiles: eligibleCandidates
    });
  }, [
    duplicateCandidateProfileIds,
    selectedCandidatesFullData,
    onRemoveSelectedCandidate,
    onSelectedCandidatesNext,
    sourcingChannelSelectionEnabled,
    organization?.channelsEnabled
  ]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <Header
        title={`Invite ${selectedCandidatesColumnData.length} candidate${
          selectedCandidatesColumnData.length !== 1 ? 's' : ''
        }`}
        subtitle={`To apply to the ${getAnalysisTitle(candidateBatch, 'job')}`}
        breadcrumbs={[{ name: 'Candidates', action: handleBack }]}
        onBack={handleBack}
        currentBreadcrumb="Invite to apply"
        actions={
          <Button
            variant="primary"
            onClick={handleConfirmAndContinue}
            disabled={!selectedCandidatesColumnData.length}
          >
            Confirm and continue
          </Button>
        }
      />
      {duplicateCandidateProfileIds.length > 0 && (
        <AlertCard
          type="warning"
          title={
            duplicateCandidateProfileIds.length ===
            selectedCandidatesColumnData.length
              ? 'All selected candidates are already in this analysis and cannot be invited again.'
              : `${duplicateCandidateProfileIds.length} of ${
                  selectedCandidatesColumnData.length
                } candidates ${
                  duplicateCandidateProfileIds.length > 1 ? 'have' : 'has'
                } already been invited or applied to this analysis and will not be included.`
          }
          sx={{ mb: 2 }}
        />
      )}
      <TableV3
        tableId="sourcing-internal-selected-candidate-profiles"
        columns={columns}
        data={selectedCandidatesColumnData}
        defaultSort={'unset'}
        loading={isSelectedCandidatesLoading}
      />
    </>
  );
};
